<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import type { SidebarAction } from '#layers/ui/components/Sidebar/Header/HeaderActions.vue';

import { refDebounced } from '@vueuse/core';
import { computed, ref } from 'vue';
import { isNullishOrEmpty } from '#layers/base/public';

const {
	actions,
	class: className,
} = defineProps<{
	actions?: SidebarAction[];
	class?: HTMLAttributes['class'];
	active?: boolean;
}>();

const isActionsMenuOpen = ref(false);
const debouncedIsActionsMenuOpen = refDebounced(isActionsMenuOpen, 200);

const onlyDropdown = computed(() => (actions?.length ?? 0) > 1);
const mainAction = computed(() => onlyDropdown.value ? null : actions?.[0]);
const hiddenActions = computed(() => onlyDropdown.value ? actions : actions?.slice(1));

function openActionsMenu() {
	isActionsMenuOpen.value = true;
}
</script>

<template>
	<div
		class="
			relative flex w-0 shrink items-center gap-1 self-end opacity-0 transition-opacity duration-150
			group-focus-within:w-fit group-focus-within:opacity-100
			group-hover:w-fit group-hover:opacity-100
			has-aria-expanded:w-fit has-aria-expanded:opacity-100
		"
		:class="{ 'w-fit opacity-100': debouncedIsActionsMenuOpen }"
	>
		<DTooltip v-if="mainAction" :text="mainAction.label">
			<DButton
				variant="ghost"
				color="neutral"
				:icon="mainAction.icon"
				size="xs"
				class="rounded-sm focus-visible:ring-inset"
				:class="[className, { 'bg-accented': active }]"
				@click.stop.prevent="mainAction.onSelect"
			/>
		</DTooltip>
		<DDropdownMenu
			v-if="!isNullishOrEmpty(hiddenActions)"
			v-model:open="isActionsMenuOpen"
			:items="hiddenActions"
			:content="{ side: 'bottom' }"
		>
			<DTooltip :text="$t('common.more')">
				<DButton
					:class="[className, { 'text-toned': active, 'text-muted': !active }]"
					variant="ghost"
					color="neutral"
					icon="lucide:ellipsis"
					size="xs"
					class="
						rounded-sm bg-transparent
						hover:bg-transparent hover:text-highlighted
						focus-visible:text-highlighted focus-visible:ring-inset
						active:bg-transparent
					"
					@click.stop="openActionsMenu"
				/>
			</DTooltip>
		</DDropdownMenu>
	</div>
</template>
